import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Calendar as CalIcon, Check, Pencil, X, ExternalLink } from "lucide-react";
import api, { formatErr } from "../lib/api";
import { BRAND_COLORS, withAlpha } from "../lib/bookingStatus";

/**
 * Feb 2026 · User-Directive „Direkt-Zuweisen im Sync-Chip" (3 Sep 2026).
 *
 * Klickbarer goldener Chip auf der Request-Anything-Detail-Seite. Zeigt
 * an, welche Felder für den Bookings-Sync fehlen (Zuweisung + Datum) und
 * öffnet inline einen Concierge-Selector + Datepicker. Ein Klick auf
 * „Speichern" schickt beide Felder in einem einzigen PATCH auf
 * `/requests/{id}/assign` (Backend nimmt jetzt `assigned_to` UND
 * `date_needed` entgegen).
 *
 * Props:
 *   request  — Original-Anfrage (mit source, assigned_to, date_needed)
 *   onSaved  — (updatedRequest) => void, wird nach erfolgreichem PATCH
 *              gefeuert. Der Aufrufer aktualisiert seinen State damit.
 */
export default function InlineSyncAssignChip({ request, onSaved }) {
  const [open, setOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [assignee, setAssignee] = useState(request?.assigned_to || "");
  const [dateStr, setDateStr] = useState(request?.date_needed || "");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setAssignee(request?.assigned_to || "");
    setDateStr(request?.date_needed || "");
  }, [request?.assigned_to, request?.date_needed]);

  useEffect(() => {
    if (!open || members.length) return;
    (async () => {
      try {
        const { data } = await api.get("/team/members");
        setMembers(
          (data || [])
            .filter((m) => ["admin", "concierge"].includes(m.role))
            .sort((a, b) => {
              if (a.role !== b.role) return a.role === "admin" ? -1 : 1;
              return (a.name || a.email || "").localeCompare(b.name || b.email || "");
            })
        );
      } catch { /* silent */ }
    })();
  }, [open, members.length]);

  const missing = [];
  if (!request?.assigned_to) missing.push("Zuweisung");
  if (!request?.date_needed) missing.push("Datum");

  const canSave = !!assignee && !!dateStr && !busy;

  const submit = async () => {
    if (!canSave) return;
    setBusy(true);
    try {
      const { data } = await api.patch(`/requests/${request.id}/assign`, {
        assigned_to: assignee,
        date_needed: dateStr,
      });
      // Feb 2026 · Sonderwunsch-Promotion Debug (5 Sep 2026 · User-Directive):
      // Zeige explizit im Toast, unter welcher Kachel die Buchung jetzt zu
      // finden ist ("In Betreuung", weil /assign den Status auto-advanced
      // von "new" -> "in_progress"). Deep-Link oeffnet das Booking-Modal
      // direkt und wechselt bei Bedarf den Filter-Modus.
      toast.success(
        (t) => (
          <div className="flex items-start gap-3">
            <div className="flex-1 leading-snug">
              <div className="font-medium text-navy">Zuweisung + Datum gespeichert.</div>
              <div className="text-[12px] text-navy-dark/70 mt-0.5">
                Erscheint jetzt in der Buchungsliste unter <b>„In Betreuung"</b>.
              </div>
            </div>
            <a
              data-testid="request-detail-toast-open-booking"
              href={`/team/bookings?open=srb-${request.id}&mode=in_progress`}
              className="shrink-0 inline-flex items-center gap-1 text-[11px] tracking-widest uppercase text-navy hover:text-gold whitespace-nowrap px-2 py-1 border border-navy/20 hover:border-gold"
              onClick={() => toast.dismiss(t)}
            >
              Öffnen <ExternalLink className="w-3 h-3" strokeWidth={1.8} />
            </a>
          </div>
        ),
        { duration: 6000 },
      );
      onSaved?.(data);
      setOpen(false);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  return (
    <div
      data-testid="request-detail-bookings-sync-hint"
      data-missing={missing.join(",")}
      data-open={open ? "1" : "0"}
      className="border"
      style={{
        borderColor: withAlpha(BRAND_COLORS.gold, 0.55),
        background: withAlpha(BRAND_COLORS.gold, 0.10),
      }}
      role="region"
    >
      <button
        type="button"
        data-testid="request-detail-bookings-sync-hint-btn"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-start gap-3 px-4 py-3 text-left hover:bg-gold/[0.06] transition-colors"
        style={{ color: "#7A5A15" }}
      >
        <CalIcon className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold }} />
        <div className="min-w-0 leading-snug flex-1">
          <div className="label-caps text-[10.5px] mb-1" style={{ color: BRAND_COLORS.gold }}>
            Noch nicht in Buchungen
          </div>
          <div className="text-[13px]">
            Wird als Buchung angezeigt, sobald <b>zugewiesen + Datum</b> gesetzt sind.
            <span className="ml-1 text-navy-dark/70">
              Fehlt aktuell: <b>{missing.join(" + ") || "—"}</b>.
            </span>
            <span className="block mt-1 text-[11.5px] text-navy-dark/60 italic">
              Nach dem Speichern erscheint die Buchung unter „In Betreuung" — der Status wechselt automatisch von „Neu" zu „In Betreuung".
            </span>
          </div>
        </div>
        <Pencil className="w-3.5 h-3.5 mt-1 shrink-0" strokeWidth={1.8} style={{ color: BRAND_COLORS.gold, opacity: 0.7 }} />
      </button>

      {open && (
        <div
          className="border-t px-4 py-4 space-y-3"
          style={{ borderColor: withAlpha(BRAND_COLORS.gold, 0.4) }}
          data-testid="request-detail-bookings-sync-panel"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="label-caps text-[10.5px] mb-1.5 block" style={{ color: BRAND_COLORS.gold }}>
                Concierge
              </label>
              <select
                data-testid="request-detail-assign-concierge"
                value={assignee}
                onChange={(e) => setAssignee(e.target.value)}
                disabled={busy}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              >
                <option value="">— auswählen —</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name || m.email} {m.role === "admin" ? "· Admin" : ""}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="label-caps text-[10.5px] mb-1.5 block" style={{ color: BRAND_COLORS.gold }}>
                Wunschdatum
              </label>
              <input
                type="date"
                data-testid="request-detail-assign-date"
                value={dateStr}
                onChange={(e) => setDateStr(e.target.value)}
                disabled={busy}
                className="w-full bg-white border border-navy/20 focus:border-gold focus:outline-none px-3 py-2 text-[13px] text-navy"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 pt-1">
            <button
              type="button"
              data-testid="request-detail-assign-save"
              onClick={submit}
              disabled={!canSave}
              className="inline-flex items-center gap-1.5 text-ivory px-4 py-2 text-[11px] tracking-[0.15em] uppercase hover:opacity-90 disabled:opacity-40"
              style={{ background: BRAND_COLORS.navy }}
            >
              <Check className="w-3.5 h-3.5" strokeWidth={2} />
              {busy ? "Speichere…" : "Speichern"}
            </button>
            <button
              type="button"
              data-testid="request-detail-assign-cancel"
              onClick={() => setOpen(false)}
              disabled={busy}
              className="inline-flex items-center gap-1 text-[11px] tracking-[0.14em] uppercase text-navy-dark/60 hover:text-navy px-2 py-1"
            >
              <X className="w-3.5 h-3.5" strokeWidth={1.8} />
              Abbrechen
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
